class OnhowProductCarousel extends HTMLElement {
  connectedCallback() {
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.load();
      },
      { rootMargin: '0px 0px 1000px 0px' }
    );
    this.observer.observe(this);

    // Keeps the "Bereits im Warenkorb" badges in sync when the cart changes
    // from somewhere else on the page (e.g. removed via the cart drawer, or
    // added through a different carousel instance further down the page).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCartBadges());
    }
  }

  formatMoney(cents) {
    return (Number(cents) / 100).toFixed(2).replace('.', ',') + ' €';
  }

  resizeImage(url, width) {
    if (!url) return '';
    return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
  }

  // Product titles here always end with the size in parentheses, e.g.
  // "Vintage Nike Trainingshose (L)". Items without a size (accessories
  // like sunglasses) have no trailing group and are treated as size-agnostic.
  parseTitle(title) {
    const match = (title || '').match(/^(.*?)\s*\(([^()]+)\)\s*$/);
    if (match) {
      return { base: match[1].trim(), size: match[2].trim().toUpperCase() };
    }
    return { base: (title || '').trim(), size: null };
  }

  // Shares the same underlying feed as the cart cross-sell widget, but under
  // its own cache key/shape (this feed keeps the raw per-product objects
  // instead of the cart widget's slimmed-down copy) to keep the two features
  // independent.
  async fetchFeed() {
    const cacheKey = 'onhowProductFeedV1';
    const cacheTtlMs = 10 * 60 * 1000;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.ts < cacheTtlMs) return parsed.products;
      }
    } catch (error) {
      // sessionStorage unavailable or corrupt cache — refetch below
    }

    let rawProducts = [];
    try {
      const first = await fetch('/collections/all?view=metafields-feed').then((res) => res.json());
      rawProducts = first.products || [];
      const totalPages = first.totalPages || 1;
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => i + 2).map((page) =>
            fetch(`/collections/all?view=metafields-feed&page=${page}`)
              .then((res) => res.json())
              .then((data) => data.products || [])
              .catch(() => [])
          )
        );
        rawProducts = rawProducts.concat(rest.flat());
      }
    } catch (error) {
      console.error(error);
    }

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), products: rawProducts }));
    } catch (error) {
      // storage full or unavailable — cache is best-effort only
    }

    return rawProducts;
  }

  async fetchCart() {
    try {
      return await fetch('/cart.js').then((res) => res.json());
    } catch (error) {
      return { items: [] };
    }
  }

  async fetchSimilarSeed(productId, limit) {
    try {
      const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`);
      const data = await res.json();
      return data.products || [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async refreshCartBadges() {
    const cards = this.querySelectorAll('.onhow-product-carousel__card');
    if (!cards.length) return;

    const cart = await this.fetchCart();
    const cartProductIds = new Set((cart.items || []).map((item) => item.product_id));

    cards.forEach((card) => {
      const media = card.querySelector('.onhow-product-carousel__media');
      if (!media) return;
      const badge = media.querySelector('.onhow-product-carousel__badge');
      const inCart = cartProductIds.has(Number(card.dataset.productId));

      if (inCart && !badge) {
        media.insertAdjacentHTML(
          'beforeend',
          '<span class="onhow-product-carousel__badge">&#10003; Bereits im Warenkorb</span>'
        );
      } else if (!inCart && badge) {
        badge.remove();
      }
    });
  }

  async load() {
    const mode = this.dataset.mode;
    const currentProductId = Number(this.dataset.productId);
    const limit = Number(this.dataset.limit) || 8;

    // Kick off every network call this mode could need up front, in
    // parallel — awaiting the feed/cart before starting the "similar" mode's
    // recommendations fetch was serializing two round-trips back-to-back,
    // the same ~1s+ delay already fixed this way in the cart cross-sell.
    const needsSimilarSeed = mode !== 'vendor' && mode !== 'recent';
    const [feed, cart, similarCandidates] = await Promise.all([
      this.fetchFeed(),
      this.fetchCart(),
      needsSimilarSeed ? this.fetchSimilarSeed(currentProductId, 10) : Promise.resolve([]),
    ]);
    const feedById = new Map(feed.map((p) => [Number(p.id), p]));
    const cartProductIds = new Set((cart.items || []).map((item) => item.product_id));

    let products = [];

    if (mode === 'vendor') {
      const vendor = this.dataset.vendor;
      const matches = feed.filter(
        (p) => p.vendor === vendor && Number(p.id) !== currentProductId && p.available
      );
      // Shuffle for variety — everything here is a one-off vintage piece, so
      // there's no "bestseller" ordering signal worth preserving.
      for (let i = matches.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [matches[i], matches[j]] = [matches[j], matches[i]];
      }
      products = matches.slice(0, limit).map((p) => ({
        id: p.id,
        title: p.title,
        image: p.image,
        handle: p.handle,
        variant_id: p.variant_id,
        price: p.price,
      }));

      // Same-gender/-size matches within a single vendor can run out fast for
      // a small brand catalog — widen to vendor-only (already the case above
      // since vendor mode has no gender/size filter), nothing further to do.
    } else if (mode === 'recent') {
      // Plain recency list from localStorage (set on every product page
      // view). No gender/size filtering and no shuffling — order here is
      // meaningful (most recently viewed first). If nothing else has been
      // viewed yet, this simply stays empty and the whole carousel removes
      // itself below.
      let recentIds = [];
      try {
        recentIds = JSON.parse(localStorage.getItem('onhowRecentlyViewedV1') || '[]');
      } catch (error) {
        recentIds = [];
      }
      const seen = new Set();
      for (const id of recentIds) {
        if (products.length >= limit) break;
        const numId = Number(id);
        if (numId === currentProductId || seen.has(numId)) continue;
        const feedEntry = feedById.get(numId);
        if (!feedEntry || !feedEntry.available) continue;
        seen.add(numId);
        products.push({
          id: feedEntry.id,
          title: feedEntry.title,
          image: feedEntry.image,
          handle: feedEntry.handle,
          variant_id: feedEntry.variant_id,
          price: feedEntry.price,
        });
      }
    } else {
      // "similar" mode: seeded by Shopify's own recommendation engine, then
      // filtered to match the viewed product's gender + size.
      const currentFeedEntry = feedById.get(currentProductId);
      const currentGender = currentFeedEntry ? currentFeedEntry.gender : null;
      const currentSize = this.parseTitle(this.dataset.title).size;

      const genderMatches = (gender) =>
        !currentGender ||
        currentGender === 'Unisex' ||
        gender == null ||
        gender === 'Unisex' ||
        gender === currentGender;

      const isEligible = (id, gender, title, available) => {
        if (!available || Number(id) === currentProductId) return false;
        if (!genderMatches(gender)) return false;
        if (!currentSize) return true;
        const { size } = this.parseTitle(title);
        return size === null || size === currentSize;
      };

      const candidates = similarCandidates;
      const seen = new Set();
      for (const c of candidates) {
        if (products.length >= limit) break;
        const feedEntry = feedById.get(Number(c.id));
        const gender = feedEntry ? feedEntry.gender : null;
        if (seen.has(c.id) || !isEligible(c.id, gender, c.title, c.available)) continue;
        seen.add(c.id);
        products.push({
          id: c.id,
          title: c.title,
          image: c.featured_image,
          handle: c.handle,
          variant_id: c.variants && c.variants[0] ? c.variants[0].id : null,
          price: c.price,
        });
      }

      if (products.length < limit) {
        const fallback = feed.filter(
          (p) => !seen.has(p.id) && isEligible(p.id, p.gender, p.title, p.available)
        );
        for (let i = fallback.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [fallback[i], fallback[j]] = [fallback[j], fallback[i]];
        }
        for (const p of fallback) {
          if (products.length >= limit) break;
          seen.add(p.id);
          products.push({
            id: p.id,
            title: p.title,
            image: p.image,
            handle: p.handle,
            variant_id: p.variant_id,
            price: p.price,
          });
        }
      }
    }

    if (!products.length) {
      this.remove();
      return;
    }

    const track = this.querySelector('.onhow-product-carousel__track');
    track.innerHTML = products
      .map((p) => this.renderCard({ ...p, inCart: cartProductIds.has(Number(p.id)) }))
      .join('');
    track.querySelectorAll('.onhow-product-carousel__add').forEach((btn) => {
      btn.addEventListener('click', (event) => this.addProduct(event, btn));
    });
  }

  renderCard(product) {
    const image = this.resizeImage(product.image, 300);
    const { base, size } = this.parseTitle(product.title);
    const safeBase = (base || product.title || '').replace(/"/g, '&quot;');
    const url = product.handle ? `/products/${product.handle}` : '#';
    return `
      <li class="onhow-product-carousel__card" data-product-id="${product.id}">
        <div class="onhow-product-carousel__media">
          <a href="${url}" class="onhow-product-carousel__image-link" tabindex="-1" aria-hidden="true">
            <img
              class="onhow-product-carousel__image"
              src="${image}"
              alt="${safeBase}"
              loading="lazy"
              width="200"
              height="260"
            >
          </a>
          ${
            product.inCart
              ? '<span class="onhow-product-carousel__badge">&#10003; Bereits im Warenkorb</span>'
              : ''
          }
        </div>
        <a href="${url}" class="onhow-product-carousel__link">
          <p class="onhow-product-carousel__title">${safeBase}</p>
          ${size ? `<p class="onhow-product-carousel__size">Größe ${size}</p>` : ''}
          <p class="onhow-product-carousel__price">${this.formatMoney(product.price)}</p>
        </a>
        ${
          product.variant_id
            ? `<button type="button" class="onhow-product-carousel__add" data-variant-id="${product.variant_id}" aria-label="In den Warenkorb"><span>+</span></button>`
            : ''
        }
      </li>
    `;
  }

  async addProduct(event, btn) {
    event.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', btn.dataset.variantId);
      formData.append('quantity', '1');

      if (cartDrawer && cartDrawer.getSectionsToRender) {
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      window.__cartMutationSeq = (window.__cartMutationSeq || 0) + 1;
      const requestSeq = window.__cartMutationSeq;

      const res = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const json = await res.json();

      if (json.status) {
        btn.classList.add('is-error');
        setTimeout(() => btn.classList.remove('is-error'), 1200);
        return;
      }

      if (cartDrawer && cartDrawer.renderContents && requestSeq === window.__cartMutationSeq) {
        cartDrawer.renderContents(json);
      }

      const card = btn.closest('.onhow-product-carousel__card');
      const media = card ? card.querySelector('.onhow-product-carousel__media') : null;
      if (media && !media.querySelector('.onhow-product-carousel__badge')) {
        media.insertAdjacentHTML('beforeend', '<span class="onhow-product-carousel__badge">&#10003; Bereits im Warenkorb</span>');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'onhow-product-carousel',
          cartData: json,
          variantId: btn.dataset.variantId,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  }
}

customElements.define('onhow-product-carousel', OnhowProductCarousel);
